/**
 * useTTS Hook
 * 
 * Manages text-to-speech for the Symptom Checker conversation.
 * Wraps unified voice services (Deepgram Aura and OpenAI TTS via Firebase).
 * 
 * Features:
 * - Speaking state tracking (isSpeaking, currentText)
 * - Echo filter integration (tracks what was said for filtering)
 * - Callback management (onStart, onEnd, onError)
 * - Prevents concurrent speech
 * - Input gating support (acceptingInputRef)
 * 
 * Adapted from feat/symptom-checker to use unified voice services instead of MSI Voice
 */

import { useRef, useCallback, useState } from 'react';
import { speak as openAISpeak, stopSpeaking as openAIStopSpeaking, setConfig as setOpenAIConfig, unlockAudioContext } from 'services/ttsService';
import { deepgramTTSService, geminiFlashTTSService } from 'services/voice';
import { ECHO_COOLDOWN_MS } from './constants';

// TTS Provider types (matching ttsFactory)
export const TTS_PROVIDER = {
  OPENAI: 'openai',
  DEEPGRAM: 'deepgram',
  GEMINI_FLASH_TTS: 'gemini_flash_tts',
};

/**
 * TTS hook for voice conversations
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.provider - TTS provider ('openai' | 'deepgram' | 'gemini_flash_tts')
 * @param {string} options.voice - Voice to use
 * @param {number} options.speed - Speech speed (0.25-4.0 for OpenAI)
 * @param {Function} options.onSpeechStart - Called when TTS starts
 * @param {Function} options.onSpeechEnd - Called when TTS ends
 * @param {Function} options.onError - Called on TTS error
 * @param {Object} options.acceptingInputRef - Ref to control input gating
 * @param {Object} options.echoFilter - Echo filter hook instance
 */
export function useTTS(options = {}) {
  const {
    provider = TTS_PROVIDER.OPENAI,
    voice = 'shimmer',
    speed = 0.95,
    onSpeechStart,
    onSpeechEnd,
    onError,
    acceptingInputRef,
    echoFilter,
  } = options;
  
  // State
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentText, setCurrentText] = useState('');
  
  // Refs
  const spokenUtterancesRef = useRef([]); // Track what was said (for echo filtering)
  const isSpeakingRef = useRef(false);
  const providerRef = useRef(provider);
  const voiceRef = useRef(voice);
  const speedRef = useRef(speed);
  
  // Keep refs in sync
  providerRef.current = provider;
  voiceRef.current = voice;
  speedRef.current = speed;
  
  // Callback refs to avoid stale closures
  const onSpeechStartRef = useRef(onSpeechStart);
  const onSpeechEndRef = useRef(onSpeechEnd);
  const onErrorRef = useRef(onError);
  
  onSpeechStartRef.current = onSpeechStart;
  onSpeechEndRef.current = onSpeechEnd;
  onErrorRef.current = onError;
  
  /**
   * Handle TTS start - called when audio begins playing
   */
  const handleTTSStart = useCallback(() => {
    setIsSpeaking(true);
    isSpeakingRef.current = true;
    
    // Gate input during TTS
    if (acceptingInputRef) {
      acceptingInputRef.current = false;
    }
    
    // Start echo cooldown
    if (echoFilter) {
      echoFilter.startEchoCooldown();
    }
    
    onSpeechStartRef.current?.();
  }, [acceptingInputRef, echoFilter]);
  
  /**
   * Handle TTS end - called when audio finishes playing
   */
  const handleTTSEnd = useCallback(() => {
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    setCurrentText('');
    
    // End echo cooldown with delay before accepting input again
    if (echoFilter) {
      echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
    }
    
    // Resume accepting input after cooldown
    if (acceptingInputRef) {
      setTimeout(() => {
        acceptingInputRef.current = true;
      }, ECHO_COOLDOWN_MS);
    }
    
    onSpeechEndRef.current?.();
  }, [acceptingInputRef, echoFilter]);
  
  /**
   * Handle TTS error
   */
  const handleTTSError = useCallback((error) => {
    console.error('[useTTS] ❌ TTS error:', error);
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    setCurrentText('');
    
    // Still end cooldown on error
    if (echoFilter) {
      echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
    }
    
    // Resume accepting input
    if (acceptingInputRef) {
      setTimeout(() => {
        acceptingInputRef.current = true;
      }, ECHO_COOLDOWN_MS);
    }
    
    onErrorRef.current?.(error);
  }, [acceptingInputRef, echoFilter]);

  /**
   * Stop any current speech
   */
  const stop = useCallback(() => {
    try {
      openAIStopSpeaking();
    } catch (e) {
      // Ignore
    }

    try {
      deepgramTTSService.stop();
    } catch (e) {
      // Ignore
    }

    try {
      geminiFlashTTSService.stop();
    } catch (e) {
      // Ignore
    }

    setIsSpeaking(false);
    isSpeakingRef.current = false;
    setCurrentText('');

    if (acceptingInputRef) {
      acceptingInputRef.current = true;
    }
  }, [acceptingInputRef]);

  /**
   * Speak text using the configured TTS provider
   * @param {string} text - Text to speak
   * @param {Object} speakOptions - Additional options
   * @returns {Promise<Object>} - Result with provider info
   */
  const speak = useCallback(async (text, speakOptions = {}) => {
    if (!text || text.trim().length === 0) {
      console.warn('[useTTS] Empty text, skipping');
      return { success: false, error: 'Empty text' };
    }
    
    // Prevent concurrent speech
    if (isSpeakingRef.current) {
      console.warn('[useTTS] ⚠️ Already speaking, stopping previous');
      stop();
    }
    
    const activeProvider = speakOptions.provider || providerRef.current;
    const activeVoice = speakOptions.voice || voiceRef.current;
    const activeSpeed = speakOptions.speed || speedRef.current;
    
    
    // Update state
    setCurrentText(text);
    
    // Add to spoken utterances (for echo filtering)
    spokenUtterancesRef.current.push(text);
    if (spokenUtterancesRef.current.length > 5) {
      spokenUtterancesRef.current.shift();
    }
    
    // Add to echo filter
    if (echoFilter) {
      echoFilter.addSystemUtterance(text);
    }
    
    // Gate input BEFORE starting TTS
    if (acceptingInputRef) {
      acceptingInputRef.current = false;
    }
    
    try {
      if (activeProvider === TTS_PROVIDER.DEEPGRAM) {
        // Use Deepgram Aura TTS
        deepgramTTSService.configure({ voice: activeVoice });
        deepgramTTSService.setHandlers({
          onStart: handleTTSStart,
          onEnd: handleTTSEnd,
          onError: handleTTSError,
        });
        
        await deepgramTTSService.speak(text);
        return { success: true, provider: TTS_PROVIDER.DEEPGRAM };
      }

      if (activeProvider === TTS_PROVIDER.GEMINI_FLASH_TTS) {
        const voiceName = (speakOptions.voice || activeVoice || 'Aoede').trim() || 'Aoede';
        geminiFlashTTSService.configure({
          apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY,
          voiceName,
        });
        await geminiFlashTTSService.speak(text, {
          onStart: handleTTSStart,
          onEnd: handleTTSEnd,
          onError: handleTTSError,
        });
        return { success: true, provider: TTS_PROVIDER.GEMINI_FLASH_TTS };
      }

      // Use OpenAI TTS via Firebase function
      setOpenAIConfig({ voice: activeVoice, speed: activeSpeed });

      // Unlock audio context (helps with browser autoplay policies)
      unlockAudioContext();

      await openAISpeak(text, 'en-US', {
        onStart: handleTTSStart,
        onEnd: handleTTSEnd,
        onError: handleTTSError,
      });

      return { success: true, provider: TTS_PROVIDER.OPENAI };
    } catch (error) {
      console.error('[useTTS] Speak failed:', error);
      handleTTSError(error);
      return { success: false, error: error.message, provider: activeProvider };
    }
  }, [handleTTSStart, handleTTSEnd, handleTTSError, acceptingInputRef, echoFilter, stop]);

  /**
   * Get list of recently spoken utterances (for echo filtering)
   */
  const getSpokenUtterances = useCallback(() => {
    return [...spokenUtterancesRef.current];
  }, []);
  
  /**
   * Clear spoken utterances history
   */
  const clearSpokenUtterances = useCallback(() => {
    spokenUtterancesRef.current = [];
  }, []);
  
  /**
   * Add text to spoken utterances (for external echo filtering)
   */
  const addSpokenUtterance = useCallback((text) => {
    spokenUtterancesRef.current.push(text);
    if (spokenUtterancesRef.current.length > 5) {
      spokenUtterancesRef.current.shift();
    }
    
    // Also add to echo filter if available
    if (echoFilter) {
      echoFilter.addSystemUtterance(text);
    }
  }, [echoFilter]);
  
  return {
    // State
    isSpeaking,
    isSpeakingRef, // Expose ref for sync checks
    currentText,
    
    // Actions
    speak,
    stop,
    
    // Echo filter helpers
    getSpokenUtterances,
    clearSpokenUtterances,
    addSpokenUtterance,
    
    // Provider info
    TTS_PROVIDER,
  };
}

export default useTTS;
